/**
 * A storage wrapper that cannot throw.
 *
 * The guest journey runs in whatever browser the QR code was scanned with -
 * often a camera app's in-app webview, sometimes in private mode, sometimes with
 * storage blocked entirely. `localStorage` throws on access in several of those,
 * and a thrown exception during the first paint of a storefront is the worst
 * possible failure for a three-minute journey. So every read degrades to `null`
 * and every write degrades to a no-op: the app keeps working, it just forgets.
 */

const MEMORY = new Map<string, string>();

function backing(): Storage | null {
  try {
    const store = globalThis.localStorage;
    const probe = '__cib_probe__';
    store.setItem(probe, '1');
    store.removeItem(probe);
    return store;
  } catch {
    return null;
  }
}

export function readString(key: string): string | null {
  const store = backing();
  if (store === null) return MEMORY.get(key) ?? null;
  try {
    return store.getItem(key);
  } catch {
    return MEMORY.get(key) ?? null;
  }
}

export function writeString(key: string, value: string): void {
  MEMORY.set(key, value);
  const store = backing();
  if (store === null) return;
  try {
    store.setItem(key, value);
  } catch {
    /* storage full or blocked: the in-memory copy is the fallback */
  }
}

export function removeKey(key: string): void {
  MEMORY.delete(key);
  const store = backing();
  if (store === null) return;
  try {
    store.removeItem(key);
  } catch {
    /* nothing to do: the value is already gone from the in-memory copy */
  }
}

/** Reads JSON, returning `null` for absent, unparseable or rejected values. */
export function readJson<T>(key: string, accept: (value: unknown) => value is T): T | null {
  const raw = readString(key);
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return accept(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function writeJson(key: string, value: unknown): void {
  try {
    writeString(key, JSON.stringify(value));
  } catch {
    /* a value that will not serialize is a bug, never a reason to break a screen */
  }
}
